import { z } from 'zod';

import { tenantStatusSchema } from './me.js';
import { roleSchema, scopeTypeSchema } from './roles.js';

export const slugSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/, { error: 'Sólo minúsculas, números y guiones' });

const isoDate = z.string();

// ---------- Clientes ----------
export const clientSchema = z.object({
  id: z.uuid(),
  agencyId: z.uuid(),
  slug: z.string(),
  name: z.string(),
  /** Todo el equipo del cliente tiene que usar verificación en dos pasos. */
  requireMfa: z.boolean(),
  createdAt: isoDate,
});
export type Client = z.infer<typeof clientSchema>;

export const createClientSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(120),
  agencyId: z.uuid().optional(),
});
export const updateClientSchema = createClientSchema
  .omit({ agencyId: true })
  .partial()
  .extend({ requireMfa: z.boolean().optional() });

// ---------- Empresas (tenants) ----------
export const featureFlagsSchema = z.record(z.string(), z.boolean());

export const tenantSchema = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  slug: z.string(),
  name: z.string(),
  status: tenantStatusSchema,
  timezone: z.string(),
  engineLocation: z.string(),
  featureFlags: featureFlagsSchema,
  createdAt: isoDate,
});
export type Tenant = z.infer<typeof tenantSchema>;

export const createTenantSchema = z.object({
  clientId: z.uuid(),
  slug: slugSchema,
  name: z.string().trim().min(1).max(120),
  status: tenantStatusSchema.default('onboarding'),
  timezone: z.string().min(1).max(64).default('Europe/Madrid'),
  engineLocation: z.string().min(1).max(64).default('shared'),
  featureFlags: featureFlagsSchema.default({}),
});
export type CreateTenant = z.input<typeof createTenantSchema>;
export const updateTenantSchema = z
  .object({
    clientId: z.uuid(),
    name: z.string().trim().min(1).max(120),
    status: tenantStatusSchema,
    timezone: z.string().min(1).max(64),
    engineLocation: z.string().min(1).max(64),
    featureFlags: featureFlagsSchema,
  })
  .partial();
export type UpdateTenant = z.infer<typeof updateTenantSchema>;

// ---------- Usuarios ----------
export const USER_STATUSES = ['active', 'disabled'] as const;
export const userStatusSchema = z.enum(USER_STATUSES);

export const membershipSchema = z.object({
  id: z.uuid(),
  scopeType: scopeTypeSchema,
  scopeId: z.uuid(),
  role: roleSchema,
});

export const adminUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  status: userStatusSchema,
  isSuperadmin: z.boolean(),
  totpEnabled: z.boolean(),
  lastLoginAt: isoDate.nullable(),
  createdAt: isoDate,
  memberships: z.array(membershipSchema),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const updateUserSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    status: userStatusSchema,
    isSuperadmin: z.boolean(),
  })
  .partial();

export const addMembershipSchema = z.object({
  scopeType: scopeTypeSchema,
  scopeId: z.uuid(),
  role: roleSchema,
});

// ---------- Planes ----------
export const CURRENCIES = ['EUR', 'USD', 'ARS'] as const;
export const currencySchema = z.enum(CURRENCIES);
export const planIntervalSchema = z.enum(['month', 'year']);

export const planLimitsSchema = z.object({
  max_companies: z.number().int().nonnegative().nullable().optional(),
  max_users: z.number().int().nonnegative().nullable().optional(),
  max_instantly_connections: z.number().int().nonnegative().nullable().optional(),
  leads_ingested_per_month: z.number().int().nonnegative().nullable().optional(),
  leads_qualified_per_month: z.number().int().nonnegative().nullable().optional(),
  emails_sent_per_month: z.number().int().nonnegative().nullable().optional(),
  ai_budget_cents_per_month: z.number().int().nonnegative().nullable().optional(),
  storage_mb: z.number().int().nonnegative().nullable().optional(),
});

export const planSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  priceCents: z.number().int(),
  currency: currencySchema,
  interval: planIntervalSchema,
  limits: planLimitsSchema,
  features: z.record(z.string(), z.boolean()),
  isPublic: z.boolean(),
  version: z.number().int(),
  archivedAt: isoDate.nullable(),
});
export type Plan = z.infer<typeof planSchema>;

export const createPlanSchema = z.object({
  code: slugSchema,
  name: z.string().trim().min(1).max(120),
  priceCents: z.number().int().nonnegative(),
  currency: currencySchema.default('EUR'),
  interval: planIntervalSchema.default('month'),
  limits: planLimitsSchema.default({}),
  features: z.record(z.string(), z.boolean()).default({}),
  isPublic: z.boolean().default(true),
});
export type CreatePlan = z.input<typeof createPlanSchema>;
export const updatePlanSchema = createPlanSchema.omit({ code: true }).partial().extend({
  archived: z.boolean().optional(),
});

// ---------- Suscripciones ----------
export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'suspended',
  'cancelled',
] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const subscriptionSchema = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  planId: z.uuid(),
  planVersion: z.number().int(),
  status: subscriptionStatusSchema,
  billingMode: z.enum(['manual', 'provider']),
  currentPeriodStart: isoDate,
  currentPeriodEnd: isoDate,
  trialEndsAt: isoDate.nullable(),
  customPriceCents: z.number().int().nullable(),
  notes: z.string().nullable(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

export const createSubscriptionSchema = z.object({
  clientId: z.uuid(),
  planId: z.uuid(),
  status: subscriptionStatusSchema.default('active'),
  currentPeriodStart: z.iso.date(),
  currentPeriodEnd: z.iso.date(),
  trialEndsAt: z.iso.date().nullable().optional(),
  customPriceCents: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export const updateSubscriptionSchema = z
  .object({
    planId: z.uuid(),
    status: subscriptionStatusSchema,
    currentPeriodStart: z.iso.date(),
    currentPeriodEnd: z.iso.date(),
    customPriceCents: z.number().int().nonnegative().nullable(),
    notes: z.string().max(2000).nullable(),
  })
  .partial();

// ---------- Pagos manuales ----------
export const PAYMENT_METHODS = ['bank_transfer', 'cash', 'mercadopago', 'stripe', 'other'] as const;

export const paymentSchema = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  invoiceId: z.uuid().nullable(),
  amountCents: z.number().int(),
  currency: currencySchema,
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().nullable(),
  receivedAt: isoDate,
});
export type Payment = z.infer<typeof paymentSchema>;

export const registerPaymentSchema = z.object({
  clientId: z.uuid(),
  invoiceId: z.uuid().nullable().optional(),
  amountCents: z.number().int().positive(),
  currency: currencySchema,
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().max(200).nullable().optional(),
  receivedAt: z.iso.date(),
});

// ---------- Auditoría ----------
export const auditEntrySchema = z.object({
  id: z.uuid(),
  actorUserId: z.uuid().nullable(),
  tenantId: z.uuid().nullable(),
  action: z.string(),
  entity: z.string().nullable(),
  entityId: z.string().nullable(),
  ip: z.string().nullable(),
  at: isoDate,
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const idParamSchema = z.object({ id: z.uuid() });
